import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import notificationService from '../services/notificationService';

const NotificationContext = createContext(null);

const POLL_INTERVAL = 30000; // 30 seconds

export function NotificationProvider({ children }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const pollRef = useRef(null);

  // Fetch unread count (lightweight polling target)
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    const result = await notificationService.getUnreadCount();
    if (result.success && result.data) {
      setUnreadCount(result.data.unreadCount);
    }
  }, [isAuthenticated]);

  // Fetch paginated notifications
  const fetchNotifications = useCallback(async (page = 1, pageSize = 20) => {
    if (!isAuthenticated) return null;
    setIsLoading(true);
    const result = await notificationService.getNotifications(page, pageSize);
    setIsLoading(false);

    if (result.success && result.data) {
      if (page === 1) {
        setNotifications(result.data.notifications);
      } else {
        setNotifications(prev => [...prev, ...result.data.notifications]);
      }
      setUnreadCount(result.data.unreadCount);
      return result.data;
    }
    return null;
  }, [isAuthenticated]);

  // Mark single notification as read (optimistic)
  const markAsRead = useCallback(async (id) => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, isRead: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    const result = await notificationService.markAsRead(id);
    if (!result.success) {
      // Revert on failure
      fetchUnreadCount();
    }
  }, [fetchUnreadCount]);

  // Mark all as read (optimistic)
  const markAllAsRead = useCallback(async () => {
    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);

    const result = await notificationService.markAllAsRead();
    if (!result.success) {
      // Revert on failure
      fetchUnreadCount();
    }
  }, [fetchUnreadCount]);

  // Poll for unread count when authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchUnreadCount();
      pollRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL);
    } else {
      setUnreadCount(0);
      setNotifications([]);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isAuthenticated, authLoading, fetchUnreadCount]);

  const value = {
    unreadCount,
    notifications,
    isLoading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationContext;
